import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';

const faqs = [
    {
        category: "About the Oils",
        questions: [
            {
                q: "What makes a perfume oil different from a regular spray?",
                a: "Perfume oils are alcohol-free and highly concentrated. Rather than evaporating quickly like an alcohol-based spray, the oil bonds with your skin's warmth and releases slowly over hours. The result is a more intimate, longer-lasting scent that evolves naturally with your body chemistry. Not a sharp blast that fades within the hour."
            },
            {
                q: "Are your fragrances alcohol-free?",
                a: "Yes, completely. Our fragrances are pure perfume oils with no ethanol and no alcohol derived from any source. This makes them ideal for those who prefer alcohol-free products for personal, cultural, or religious reasons, and they are far gentler on the skin."
            },
            {
                q: "Is this a pure oil or is it blended?",
                a: "Our fragrances are carefully blended compositions. Pure raw oil in isolation can be an acquired and challenging scent. Our approach is to build around a rich fragrance core while surrounding it with complementary notes that make each oil wearable, balanced, and distinctive."
            },
            {
                q: "How much should I use?",
                a: "Far less than you would expect. One to two drops is all you need as our oils are highly concentrated. A small bottle will last considerably longer than a full-size alcohol spray, making them exceptional value. Less is more."
            },
        ]
    },
    {
        category: "Scent & Experience",
        questions: [
            {
                q: "What do your perfume oils actually smell like?",
                a: "Every fragrance has its own character, and we've deliberately spread them across a wide range rather than sticking to one style. Some open bright and fruity before settling into a warm floral base. Others are rooted and heavy from the first note, built around oud, incense or amber. A few lean gourmand, with coffee, tonka bean or vanilla wrapping close to the skin. We add new scents fairly often, so rather than list every one here, head to Our Scents and open any bottle's product page. Each one has its notes broken down in full so you know exactly what you're getting before you buy."
            },
            {
                q: "Does the scent evolve over time on the skin?",
                a: "Yes, and this is one of the most celebrated qualities of a concentrated perfume oil. Unlike a spray that opens sharp and fades flat, an oil unfolds in layers. The opening notes settle within the first hour, the heart develops over time, and the base deepens and stays close to your skin for hours. Each fragrance tells a different story as it wears."
            },
            {
                q: "I have never tried a perfume oil before. Where do I start?",
                a: "There's no wrong place to start. Have a look through the scent notes for each fragrance and pick whichever profile sounds closest to what you already enjoy wearing. Since our oils are highly concentrated, begin with a single drop to see how it develops on your skin before building up further, and reach out to us directly if you'd like a personal recommendation."
            },
            {
                q: "Are your scents masculine, feminine, or unisex?",
                a: "We offer scents across masculine, feminine, and unisex profiles, so there's a fit whatever your preference. These are simply a guide rather than a rule, so feel free to explore beyond your usual profile. Scent is about how it feels on you, not a label on a box."
            },
            {
                q: "Can I layer your fragrances?",
                a: "Absolutely. Layering is an art form in Arabian perfumery, and all of our scents are designed to complement each other, so any combination can be layered if you'd like to. Start light, build depth, and make the blend entirely your own."
            },
        ]
    },
    {
        category: "Authenticity & Quality",
        questions: [
            {
                q: "Is your oil natural or synthetic?",
                a: "Our fragrances are composed using high-quality fragrance compounds to ensure consistency, depth, and lasting performance. This is a deliberate choice. True wild oud is among the rarest materials on earth and its supply is under severe environmental pressure with quality that varies wildly from batch to batch. We work with premium compounds engineered to capture the richness, warmth, and fixative power that great fragrance is celebrated for. As we grow, we intend to explore a premium natural line for those who want raw uncut materials. We never use animal-derived ingredients such as deer musk or ambergris. Every musk and amber note in our perfume oils is synthetically created — so you get the same depth and warmth without the animal welfare cost."
            },
            {
                q: "Why are your oils priced the way they are?",
                a: "Raw natural oud oil commands extreme prices due to rarity and production complexity. Our pricing reflects the use of premium fragrance compounds rather than raw materials, allowing us to deliver an exceptional experience at a price that is accessible without any compromise on quality or longevity."
            },
            {
                q: "Why do some oils smell unusual at first?",
                a: "On first application, the top notes can seem sharp or intense before settling into the warmer richer heart. This opening phase typically lasts 15 to 30 minutes. What emerges after is the true character of the fragrance and it is worth waiting for."
            },
            {
                q: "Is it safe, halal, and vegan?",
                a: "Our oils contain no animal-derived ingredients and no ethanol. They are alcohol-free and formulated with skin-safe fragrance compounds. For specific certification queries, please contact us directly at support@maisondoud.com."
            },
        ]
    },
    {
        category: "Usage & Performance",
        questions: [
            {
                q: "How do I apply perfume oil properly?",
                a: "Apply one to two drops directly to your pulse points on the wrists, neck, and inner elbows. Do not rub the application points together. Let the oil absorb naturally and your body heat will activate and project the fragrance. A small dab on clothing or in the hair will extend the trail significantly."
            },
            {
                q: "How long will the scent last?",
                a: "On skin, our oils last 8 to 18 hours or more depending on your skin type and hydration. Oily or moisturised skin holds fragrance significantly longer. On clothing and fabric the same scent can linger for days as the fibres trap the oil and release it slowly over time."
            },
            {
                q: "How do I make my fragrance last even longer?",
                a: "Apply to moisturised skin. This is the single most effective step as dry skin absorbs oil rapidly and reduces longevity. Never rub after applying. A dab on clothing, fabric, or hair will extend the trail considerably and keep the scent projecting throughout the day."
            },
            {
                q: "Will it stain my clothes?",
                a: "Concentrated oils applied in large quantities can leave a faint mark on very delicate or light-coloured fabrics. Always dab lightly and never pour. Applying to pulse points and allowing a moment to absorb before dressing will prevent any transfer."
            },
            {
                q: "Is it safe for sensitive skin?",
                a: "Oil-based fragrances are generally gentler on skin than alcohol-based alternatives as they do not strip or dry the skin. If you have known sensitivities, we recommend applying a small amount to the inner wrist first and waiting before full application. If you have sensitive skin or known allergies, we recommend checking the allergen list for each scent (available on the product page) before applying directly to skin."
            },
        ]
    },
    {
        category: "Pricing, Shipping & Returns",
        questions: [
            {
                q: "How long will a bottle last?",
                a: "With one to two drops per application, a standard bottle will comfortably last several months with daily use, far longer than a typical alcohol spray. The concentration of our oils means you use far less each time."
            },
            {
                q: "Do you offer samples or testers?",
                a: <>Yes. Every order comes with a free sample of each of our scents, so you can explore the whole range alongside your full-size bottle. Take our <Link to="/quiz" className="text-[#CEAC8F] hover:underline">scent quiz</Link> first to find your best match, and if your order isn't quite right once you've tried the samples, you can return it within 14 days and reorder whichever scent you preferred instead.</>
            },
            {
                q: "Do you ship internationally?",
                a: "Yes. We ship across Ireland, the EU and the UK with free standard delivery on all orders. Express delivery is available for €9.99. For other destinations, shipping costs are calculated at checkout."
            },
            {
                q: "What is your return policy?",
                a: "We offer a 14-day return policy. Items must be unused and in their original packaging. If you try your complimentary samples and decide the scent you ordered isn't right for you, you're welcome to return your order within the 14 days and reorder your preferred scent instead. Full details are in our Refund Policy."
            },
            {
                q: "How should I store my perfume oil?",
                a: "Keep your oils in a cool dark place. A drawer or wardrobe shelf is ideal. Avoid storing near windows, radiators, or in bathrooms where temperature and humidity fluctuate. Heat, light, and moisture are the enemies of fragrance oil. Always keep the bottle tightly sealed when not in use."
            },
            {
                q: "What is the shelf life of your perfume oils?",
                a: "Our perfume oils have a shelf life of 2 to 3 years when stored correctly — kept indoors and out of direct sunlight."
            },
        ]
    },
    {
        category: "Culture & Context",
        questions: [
            {
                q: "Why is fragrance so significant in Middle Eastern culture?",
                a: "Fragrance has been used in the Arabian Peninsula, South Asia, and East Asia for centuries in religious ceremonies, as a symbol of hospitality, and as a mark of personal identity. In many cultures, a person's scent is considered part of their presence and character. Offering fragrance to a guest is a mark of respect. Wearing it is a statement. This heritage is at the heart of everything we do at MAISON D'OUD."
            },
            {
                q: "Is fragrance oil used for religious or special occasions?",
                a: "Yes. Historically, oud and incense have been used in mosques and religious ceremonies across Islam, Buddhism, and Hinduism. They are also deeply associated with weddings, celebrations, and rites of passage in many cultures. Today they are worn as everyday luxuries, but the sense of occasion they carry has never left them."
            },
            {
                q: "Does perfume oil improve with age?",
                a: "In its raw form, yes. Certain fragrance materials genuinely mature and deepen over time, much like fine wine. Our blended oils are at their best within 2 to 3 years of purchase when stored correctly — indoors and out of direct sunlight."
            },
        ]
    },
];

const FAQItem = ({ q, a }) => {
    const [open, setOpen] = useState(false);
    return (
        <div className="border-b border-border last:border-0">
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between py-5 text-left group"
            >
                <span className={`text-sm sm:text-base font-light transition-colors pr-4 ${open ? 'text-[#CEAC8F]' : 'text-foreground group-hover:text-[#CEAC8F]'}`}>
                    {q}
                </span>
                {open
                    ? <ChevronUp className="w-5 h-5 text-[#CEAC8F] flex-shrink-0" />
                    : <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                }
            </button>
            {open && (
                <p className="text-muted-foreground text-sm leading-relaxed pb-5">{a}</p>
            )}
        </div>
    );
};

const FAQ = () => {
    usePageTitle('FAQ', "Everything you need to know about MAISON D'OUD perfume oils.", 'perfume oil faq, perfume oils questions, how to use perfume oil');

    return (
        <div className="bg-background min-h-screen pt-32 pb-24 px-6 transition-colors duration-300">
            <div className="max-w-3xl mx-auto">
                <div className="text-center mb-16">
                    <h1 className="text-5xl md:text-6xl font-light text-foreground mb-4 tracking-wide">FAQ</h1>
                    <p className="text-muted-foreground text-lg">Everything you need to know about MAISON D'OUD.</p>
                </div>

                <div className="space-y-12">
                    {faqs.map((section, i) => (
                        <div key={i}>
                            <h2 className="text-[#CEAC8F] text-xs tracking-widest uppercase mb-4">{section.category}</h2>
                            <div className="bg-card rounded-2xl border border-border px-6">
                                {section.questions.map((item, j) => (
                                    <FAQItem key={j} q={item.q} a={item.a} />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-16 text-center bg-card rounded-2xl p-8 border border-border">
                    <p className="text-foreground font-light text-lg mb-2">Still have a question?</p>
                    <p className="text-muted-foreground text-sm mb-6">We're happy to help. Reach out and we'll get back to you.</p>
                    <Link
                        to="/contact"
                        className="inline-block bg-[#CEAC8F] text-black px-8 py-4 rounded-lg font-medium hover:bg-[#d4b896] transition-colors"
                    >
                        Contact Us
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default FAQ;